import type { RelayablyAssistantRole } from '@monorepo/relayably/workspaces'

export type RelayablyPlanId = 'personal' | 'professional' | 'business' | 'enterprise'
export type RelayablyNumberKind = 'mobile' | 'local' | 'tollfree'
export type RelayablyCountryCode = 'AU'

export type RelayablyMarketingPlan = {
  id: RelayablyPlanId
  name: string
  price: string
  description: string
  features: string[]
  keyAgents?: string[]
  allowedNumberKinds?: RelayablyNumberKind[]
  note?: string
  cta?: string
  highlight?: boolean
  comingSoon?: boolean
}

export type RelayablyAssistantRoleOption = {
  value: RelayablyAssistantRole
  label: string
  blurb: string
}

export type RelayablyNumberKindOption = {
  value: RelayablyNumberKind
  label: string
  requirement: string
}

export const RELAYABLY_SUPPORTED_COUNTRIES = ['AU'] as const satisfies readonly RelayablyCountryCode[]
export const RELAYABLY_DEFAULT_COUNTRY: RelayablyCountryCode = 'AU'
export const RELAYABLY_COUNTRY_LABELS: Record<RelayablyCountryCode, string> = {
  AU: 'Australia',
}
export const RELAYABLY_COUNTRY_OPTIONS = RELAYABLY_SUPPORTED_COUNTRIES.map(code => ({
  code,
  label: RELAYABLY_COUNTRY_LABELS[code],
}))

export const RELAYABLY_PLANS: RelayablyMarketingPlan[] = [
  {
    id: 'personal',
    name: 'Personal',
    price: '$19',
    description: 'Receptionist and screen agents for individuals who want a mobile-first setup.',
    features: ['Receptionist', 'Personal Assistant', 'Mobile number only'],
    keyAgents: ['Receptionist', 'Personal Assistant'],
    allowedNumberKinds: ['mobile'],
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '$49',
    description: 'Adds support and inbound sales handling for a more capable professional front door.',
    features: ['Receptionist', 'Executive Assistant', 'Customer Support', 'Inbound Sales', 'Mobile or Local numbers'],
    keyAgents: ['Receptionist', 'Executive Assistant', 'Customer Support', 'Inbound Sales'],
    allowedNumberKinds: ['mobile', 'local'],
    highlight: true,
  },
  {
    id: 'business',
    name: 'Business',
    price: '$149',
    description: 'Full commercial setup with outbound sales coverage and toll free support.',
    features: [
      'Receptionist',
      'Executive Assistant',
      'Customer Support',
      'Inbound Sales',
      'Outbound Sales',
      'Mobile, Local, or Toll Free numbers',
    ],
    keyAgents: ['Receptionist', 'Executive Assistant', 'Customer Support', 'Inbound Sales', 'Outbound Sales'],
    allowedNumberKinds: ['mobile', 'local', 'tollfree'],
    note: 'Includes up to 100 outbound connected calls per month. Additional connected calls are priced at $0.50 each.',
    comingSoon: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    description: 'For larger teams with custom needs, support, and rollout requirements.',
    features: [
      'Custom setup',
      'Support across teams',
      'Single sign-on',
      'Volume pricing',
      'Dedicated support',
      'Private deployment options',
    ],
    cta: 'Contact Us',
  },
]

export const RELAYABLY_SELF_SERVE_PLANS = RELAYABLY_PLANS.filter(plan => plan.id !== 'enterprise')

export const RELAYABLY_ASSISTANT_ROLE_OPTIONS: RelayablyAssistantRoleOption[] = [
  { value: 'business_receptionist', label: 'Business Receptionist', blurb: 'Handle general inbound calls with calm front-desk energy.' },
  { value: 'personal_assistant', label: 'Personal Assistant', blurb: 'Protect your time and route only what matters.' },
  { value: 'lead_qualifier', label: 'Lead Qualifier', blurb: 'Capture buying intent before the team steps in.' },
  { value: 'appointment_setter', label: 'Appointment Setter', blurb: 'Keep conversations moving toward the calendar.' },
  { value: 'support_triage_assistant', label: 'Support Triage', blurb: 'Sort urgency, issue type, and the next support path.' },
  { value: 'recruiter_screener', label: 'Recruiter Screener', blurb: 'Collect candidate context before a human review.' },
]

export const RELAYABLY_NUMBER_KIND_OPTIONS: RelayablyNumberKindOption[] = [
  { value: 'mobile', label: 'Mobile', requirement: 'Available on Personal and above.' },
  { value: 'local', label: 'Local', requirement: 'Available on Professional and above.' },
  { value: 'tollfree', label: 'Toll Free', requirement: 'Available on Business and above.' },
]

export const getRelayablyPlanById = (planId: RelayablyPlanId) => RELAYABLY_PLANS.find(plan => plan.id === planId)

export const isRelayablyPlanComingSoon = (planId: string) => getRelayablyPlanById(planId as RelayablyPlanId)?.comingSoon === true
